import { FC, memo, useCallback, useMemo } from 'react';

import { DetailsCard } from './card';
import { Play } from '../../../../Icons';

// Utils
import { useTranslation } from 'react-i18next';

// Redux
import { uiActions } from '../../../../../store/slices/ui';
import { useAppDispatch } from '../../../../../store/store';
import { usePlayback } from '../../../../../contexts/PlaybackContext';
import {
  deriveQueuePointer,
  extractQueueItemMetadata,
} from '../../../../../lib/playback/queueDisplay';

export const NextInQueue: FC = memo(() => {
  const [t] = useTranslation(['playingBar']);
  const dispatch = useAppDispatch();
  const { queueSession, queueItems, queueStatus, performQueueAction } = usePlayback();

  const pointer = useMemo(() => deriveQueuePointer(queueSession, queueItems), [queueItems, queueSession]);
  const nextIndex = pointer.index != null ? pointer.index + 1 : 0;
  const nextItem = queueItems[nextIndex] ?? null;
  const metadata = useMemo(() => extractQueueItemMetadata(nextItem), [nextItem]);

  const handleJump = useCallback(() => {
    if (!queueSession || queueStatus !== 'ready') {
      return;
    }
    if (!nextItem) {
      return;
    }
    void performQueueAction('jump-to-index', { targetIndex: nextIndex });
  }, [nextIndex, nextItem, performQueueAction, queueSession, queueStatus]);

  if (!nextItem) return null;

  return (
    <DetailsCard
      title={t('Next in queue')}
      extra={
        <button
          onClick={() => {
            dispatch(uiActions.toggleQueue());
          }}
          className='link-button'
        >
          {t('Open Queue')}
        </button>
      }
    >
      <div className='queue-next-item'>
        <div className='queue-next-item__text'>
          <p className='queue-next-item__title' title={metadata.title ?? undefined}>
            {metadata.displayTitle ?? metadata.title ?? `Track ${nextIndex + 1}`}
          </p>
          {metadata.artists ? (
            <p className='queue-next-item__artists' title={metadata.artists}>
              {metadata.artists}
            </p>
          ) : null}
          {metadata.recordingName || metadata.albumName ? (
            <p
              className='queue-next-item__recording'
              title={metadata.recordingName ?? metadata.albumName ?? undefined}
            >
              {metadata.recordingName ?? metadata.albumName}
            </p>
          ) : null}
        </div>
        <button
          type='button'
          className='queue-next-item__action'
          onClick={handleJump}
          disabled={queueStatus !== 'ready'}
        >
          <Play />
        </button>
      </div>
    </DetailsCard>
  );
});
